import React, {useCallback, useEffect, useState} from "react";
import {CustomFieldTO} from 'generated/JavaTO'
import _ from "lodash";
import {CFValue} from 'module/CustomField/type/CFType'
import {log} from 'common/log'
import {CFComponentProps} from 'module/CustomField/type/CFComponentType'
import {stripHTML} from 'util/StringUtil'
import {cssCFInput} from 'module/CustomField/component/input/style/cssCFInput'

interface CustomFieldNumberComponentProps extends CFComponentProps<CFValue> {
	precision?: number
	round?: number
	step?: number
}

function findCfAttrs(cfDef: CustomFieldTO): { [key: string]: number | undefined } {
	return _.transform(cfDef.cfAttrs, (res, attr) => {
		_.set(res, attr.key, isNaN(parseFloat(attr.value)) ? undefined : +attr.value)
		return res;
	}, {})
}

interface CalculateProps {
	value: CFValue;
	step?: number;
	precision?: number;
	min?: number;
	max?: number;
}

function increment({value, step = 1, precision = 0, max}: CalculateProps): string {
	let v: number = Number(value) || 0;
	v += step;
	if (max && v > max) v = max;
	return v.toFixed(precision);
}

function decrement({value, step = 1, precision = 0, min}: CalculateProps): string {
	let v: number = Number(value) || 0;
	v -= step;
	if ((min || min == 0) && v < min) v = min;
	return v.toFixed(precision);
}

function adaptValuePrecision(value: CFValue, precision: number | undefined = 0): string {
	if (value == null) return '';
	if (isNaN(+value)) return value + '';
	return parseFloat(value + '').toFixed(precision)
}

export function CustomFieldNumberComponent({modelValue, disabled, placeholderHTML, setCb, cfDef}: CustomFieldNumberComponentProps) {
	const props = findCfAttrs(cfDef) || {};
	const [viewValue, _setViewValue] = useState<CFValue>(adaptValuePrecision(modelValue, props.precision));
	const setViewValue = useCallback((value) => _setViewValue(value), []);

	useEffect(() => {
		setViewValue(adaptValuePrecision(modelValue, props.precision));
	}, [modelValue])

	const onKeyDown = useCallback((event) => {
		const {step, min, max, precision} = props;
		switch (event.code) {
			case 'ArrowUp':
				_setViewValue(increment({value: viewValue, step, precision, max}))
				break;
			case 'ArrowDown':
				_setViewValue(decrement({value: viewValue, step, precision, min}));
				break;
			case 'Escape':
				setViewValue(adaptValuePrecision(modelValue, props.precision))
				break;
			default:
				log.debug("Key: '" + event.code + "' pressed. No action defined");
		}
	}, [modelValue, viewValue, cfDef, setViewValue]);

	return (<div>
		<input
			type={'text'}
			css={cssCFInput}
			value={viewValue || ''}
			onChange={({target}) => {
				let v = _.replace(target.value, ',', '.');
				setViewValue(v);
			}}
			onFocus={(evt) => evt.target.select()}
			onBlur={() => {
				setViewValue(adaptValuePrecision(modelValue, props.precision));
				if (!disabled) {
					setCb(viewValue);
				}
			}}
			disabled={disabled}
			placeholder={stripHTML(placeholderHTML)}
			onKeyDown={onKeyDown}
		/>
	</div>)
}